import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { isEventCohost } from '../lib/firestore/eventCohosts';
import { getEvent } from '../lib/firestore/events';
import { getGroupMember } from '../lib/firestore/groups';
import { isAdmin } from '../lib/firestore/admins';
import '../styles/TopNavBar.css';

const TopNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthState();
  const { groupId, eventId } = useParams();
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [canEditEvent, setCanEditEvent] = useState(false);
  const [canEditGroup, setCanEditGroup] = useState(false);
  const [isGroupOwner, setIsGroupOwner] = useState(false);
  const settingsMenuRef = useRef(null);
  const isGroupPage = location.pathname.startsWith('/groups/') && groupId;
  const isEventPage = location.pathname.startsWith('/events/') && eventId;

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/home');
    }
  };

  const handleHome = () => {
    navigate('/home');
  };

  // Close settings menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
    };

    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSettingsMenu]);

  // Check if user can edit this event
  useEffect(() => {
    const checkEventPermissions = async () => {
      if (isEventPage && eventId && user) {
        try {
          const eventData = await getEvent(eventId);
          if (eventData) {
            // Check if user can edit:
            // 1. User is the direct host (profile events) - also check if hostId matches even if hostType is missing
            // 2. User created the event (createdBy field)
            // 3. User is an event cohost
            // 4. If event is hosted by a group: user is group owner/cohost
            const userIsHost = (eventData.hostId === user.uid && eventData.hostType === 'profile') || 
                               (eventData.hostId === user.uid && !eventData.hostType); // Fallback for old events
            const userIsCreator = eventData.createdBy === user.uid;
            const userIsCohost = await isEventCohost(eventId, user.uid);
            
            let userIsGroupOwnerOrCohost = false;
            if (eventData.hostType === 'group') {
              try {
                const member = await getGroupMember(eventData.hostId, user.uid);
                userIsGroupOwnerOrCohost = member?.role === 'owner' || member?.role === 'cohost';
              } catch (err) {
                console.warn('Error checking group membership:', err);
              }
            }
            
            const userIsAdmin = await isAdmin(user.uid);
            const canEdit = userIsHost || userIsCreator || userIsCohost || userIsGroupOwnerOrCohost || userIsAdmin;
            console.log('🔵 [TopNavBar] Event permission check:', {
              eventId,
              userId: user.uid,
              hostId: eventData.hostId,
              hostType: eventData.hostType,
              createdBy: eventData.createdBy,
              userIsHost,
              userIsCreator,
              userIsCohost,
              userIsGroupOwnerOrCohost,
              canEdit
            });
            setCanEditEvent(canEdit);
          }
        } catch (error) {
          console.error('Error checking event permissions:', error);
          setCanEditEvent(false);
        }
      } else {
        setCanEditEvent(false);
      }
    };

    checkEventPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEventPage, eventId, user?.uid]);

  // Check if user can edit this group (owner from group.ownerId OR from members; cohost from members)
  useEffect(() => {
    const checkGroupPermissions = async () => {
      if (isGroupPage && groupId && user) {
        try {
          const { getGroup } = await import('../lib/firestore/groups');
          const groupData = await getGroup(groupId);
          if (groupData) {
            const isOwnerFromDoc = groupData.ownerId === user.uid;
            let member = null;
            try {
              member = await getGroupMember(groupId, user.uid);
            } catch (e) {
              // e.g. auto-join or legacy group where owner might not be in members subcollection
            }
            const isOwner = isOwnerFromDoc || member?.role === 'owner';
            const isCohost = member?.role === 'cohost';
            const userIsAdmin = await isAdmin(user.uid);
            const canEdit = isOwner || isCohost || userIsAdmin;
            setCanEditGroup(canEdit);
            setIsGroupOwner(isOwner);
          }
        } catch (error) {
          console.error('Error checking group permissions:', error);
          setCanEditGroup(false);
          setIsGroupOwner(false);
        }
      } else {
        setCanEditGroup(false);
        setIsGroupOwner(false);
      }
    };

    checkGroupPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupPage, groupId, user?.uid]);

  // Listen for group permissions updates from GroupView
  useEffect(() => {
    const handleGroupPermissionsUpdate = (event) => {
      if (event.detail.groupId === groupId) {
        setCanEditGroup(event.detail.canEdit);
        setIsGroupOwner(event.detail.isOwner);
      }
    };

    window.addEventListener('groupPermissionsUpdate', handleGroupPermissionsUpdate);
    return () => {
      window.removeEventListener('groupPermissionsUpdate', handleGroupPermissionsUpdate);
    };
  }, [groupId]);

  // Don't show on login/signup pages or home page
  if (!user || location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/home') {
    return null;
  }

  return (
    <nav className="top-nav-bar">
      <div className="top-nav-left">
        <button className="top-nav-btn top-nav-back" onClick={handleBack} aria-label="Go back">
          ← Back
        </button>
      </div>
      <div className="top-nav-center">
        <button className="top-nav-btn top-nav-home" onClick={handleHome} aria-label="Go home" title="Hotelia">
          <span className="top-nav-logo">HOTELIA</span>
        </button>
      </div>
      {((isGroupPage && canEditGroup) || (isEventPage && canEditEvent)) && (
        <div className="top-nav-right" ref={settingsMenuRef}>
          <button
            className="top-nav-btn top-nav-settings"
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            aria-label="Settings"
          >
            ⚙️
          </button>
          {showSettingsMenu && (
            <div className="top-nav-settings-menu">
              {isGroupPage && (
                <>
                  <button
                    className="top-nav-settings-item"
                    onClick={() => {
                      setShowSettingsMenu(false);
                      navigate(`/groups/${groupId}/settings`);
                    }}
                  >
                    Edit Group
                  </button>
                  {/* Only owner can manage cohosts */}
                  {isGroupOwner && (
                    <button
                      className="top-nav-settings-item"
                      onClick={() => {
                        setShowSettingsMenu(false);
                        navigate(`/groups/${groupId}/cohosts`);
                      }}
                    >
                      Manage Cohosts
                    </button>
                  )}
                  {/* Only owner can delete group */}
                  {isGroupOwner && (
                    <button
                      className="top-nav-settings-item top-nav-settings-danger"
                      onClick={() => {
                        setShowSettingsMenu(false);
                        window.dispatchEvent(new CustomEvent('openDeleteGroupModal', { detail: { groupId } }));
                      }}
                    >
                      Delete Group
                    </button>
                  )}
                </>
              )}
              {isEventPage && (
                <>
                  <button
                    className="top-nav-settings-item"
                    onClick={() => {
                      setShowSettingsMenu(false);
                      navigate(`/events/${eventId}/edit`);
                    }}
                  >
                    Edit Event
                  </button>
                  <button
                    className="top-nav-settings-item"
                    onClick={() => {
                      setShowSettingsMenu(false);
                      navigate(`/events/${eventId}/cohosts`);
                    }}
                  >
                    Manage Cohosts
                  </button>
                  <button
                    className="top-nav-settings-item top-nav-settings-danger"
                    onClick={() => {
                      setShowSettingsMenu(false);
                      navigate(`/events/${eventId}/delete`);
                    }}
                  >
                    Delete Event
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default TopNavBar;

